import { Box, Radio, TextField, Typography } from "@mui/material";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import i18n, { common, organization } from "../../i18n";
import { StructureDeletePreview } from "../../models/StructureDelete";
import FileUtils from "../../utils/fileUtils";
import "./alert.css";

const MySwal = withReactContent(Swal);

const RED = "var(--red)";
const NAVY = "var(--dark)";
const STATUS_ORDER = ["approved", "pending", "draft", "rejected"];

const text = (key: string, options?: Record<string, unknown>): string =>
  i18n.t(key, { ns: [organization, common], ...options }) as string;

// the confirm button sits outside the React body, so the body drives it
const setConfirmButton = (label: string, color: string, enabled: boolean) => {
  const button = Swal.getConfirmButton();
  if (!button) return;
  button.textContent = label;
  button.style.backgroundColor = color;
  button.disabled = !enabled;
};

const yearRange = (preview: StructureDeletePreview) => {
  const { firstYear, lastYear } = preview.records;
  if (firstYear === null || lastYear === null) return "";
  return firstYear === lastYear ? `${firstYear}` : `${firstYear}–${lastYear}`;
};

const statusLine = (preview: StructureDeletePreview) =>
  STATUS_ORDER.filter((status) => preview.records.byStatus[status] > 0)
    .map((status) =>
      text(`Structure-Delete-Count-${status}`, {
        n: preview.records.byStatus[status],
      }),
    )
    .join(" · ");

const recordsLabel = (preview: StructureDeletePreview) => {
  const years = yearRange(preview);
  return years
    ? text("Structure-Delete-Records-Years", { years })
    : text("Structure-Delete-Records");
};

const Fact = (props: { value: number; label: string; detail?: string }) => (
  <Box
    sx={{
      bgcolor: "var(--gray3)",
      borderRadius: "8px",
      p: "10px 14px",
      textAlign: "left",
    }}
  >
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "var(--dark)" }}>
      {props.value}
    </Typography>
    <Typography
      sx={{
        fontSize: 13,
        color: "var(--gray1)",
        mt: "2px",
        lineHeight: "18px",
      }}
    >
      {props.label}
      {props.detail && (
        <>
          <br />
          {props.detail}
        </>
      )}
    </Typography>
  </Box>
);

const Facts = (props: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "10px",
      mt: "12px",
    }}
  >
    {props.children}
  </Box>
);

const Lead = (props: { lead: string; name: string; tail: string }) => (
  <>
    {props.lead}
    <strong>{props.name}</strong>
    {props.tail}
  </>
);

const Header = (props: { title: string; children: ReactNode }) => (
  <>
    <Typography
      sx={{
        fontSize: 20,
        fontWeight: 700,
        textAlign: "left",
        color: "var(--dark)",
      }}
    >
      {props.title}
    </Typography>
    <Box
      sx={{
        fontSize: 16,
        lineHeight: "24px",
        fontWeight: 500,
        textAlign: "left",
        color: "var(--dark)",
      }}
    >
      {props.children}
    </Box>
  </>
);

const Warnings = (props: { preview: StructureDeletePreview }) => {
  const approved = props.preview.records.byStatus.approved ?? 0;
  const lines: ReactNode[] = [];
  if (approved > 0) {
    lines.push(
      <li key="approved">
        <b>{text("Structure-Delete-Warn-Approved", { n: approved })}</b>
        {text("Structure-Delete-Warn-Approved-Tail")}
      </li>,
    );
  }
  for (const report of props.preview.savedReports) {
    lines.push(
      <li key={`report-${report.year}`}>
        {text("Structure-Delete-Warn-Report-Lead", { year: report.year })}
        <b>{text("Structure-Delete-Warn-Report", { n: report.count })}</b>
        {text("Structure-Delete-Warn-Report-Tail")}
      </li>,
    );
  }
  props.preview.goals.forEach((goal, index) => {
    lines.push(
      <li key={`goal-${index}`}>
        {text("Structure-Delete-Warn-Goal-Lead")}
        <b>{goal.name}</b>
        {text("Structure-Delete-Warn-Goal-Tail", { year: goal.baseYear })}
      </li>,
    );
  });
  if (lines.length === 0) return null;
  return (
    <Box
      data-testid="structure-delete-warnings"
      sx={{
        display: "flex",
        gap: "10px",
        alignItems: "flex-start",
        textAlign: "left",
        p: "10px 14px",
        borderRadius: "8px",
        bgcolor: "var(--yellow-10)",
        border: "1px solid var(--yellow-30)",
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        style={{ flex: "0 0 20px", marginTop: 2 }}
      >
        <path
          fill="var(--amber-dark)"
          d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
        />
      </svg>
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: "18px",
          fontSize: 14,
          lineHeight: "21px",
          color: "var(--dark)",
        }}
      >
        {lines}
      </Box>
    </Box>
  );
};

const TypedName = (props: {
  name: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <Box sx={{ textAlign: "left" }}>
    <Typography
      component="label"
      htmlFor="structure-delete-typed"
      sx={{ display: "block", fontSize: 14, color: "var(--dark)", mb: "6px" }}
    >
      <Lead
        lead={text("Structure-Delete-Type-Lead")}
        name={props.name}
        tail={text("Structure-Delete-Type-Tail")}
      />
    </Typography>
    <TextField
      id="structure-delete-typed"
      fullWidth
      size="small"
      autoComplete="off"
      placeholder={props.name}
      value={props.value}
      onChange={(event) => props.onChange(event.target.value)}
    />
  </Box>
);

const Undo = () => (
  <Box
    sx={{
      display: "flex",
      gap: "8px",
      alignItems: "center",
      fontSize: 14,
      fontWeight: 500,
      color: "var(--dark)",
      textAlign: "left",
    }}
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      style={{ flex: "0 0 18px" }}
    >
      <path
        fill="var(--red)"
        d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z"
      />
    </svg>
    {text("Structure-Delete-Undo")}
  </Box>
);

const Option = (props: {
  selected: boolean;
  label: string;
  description: string;
  onSelect: () => void;
}) => (
  <Box
    onClick={props.onSelect}
    sx={{
      display: "flex",
      gap: "12px",
      alignItems: "flex-start",
      p: "14px 16px",
      borderRadius: "8px",
      cursor: "pointer",
      textAlign: "left",
      border: props.selected
        ? "1.5px solid var(--green)"
        : "1px solid var(--gray2)",
      bgcolor: props.selected ? "var(--green3)" : "transparent",
    }}
  >
    <Radio
      checked={props.selected}
      onChange={props.onSelect}
      inputProps={{ "aria-label": props.label }}
      sx={{
        p: 0,
        mt: "2px",
        color: "var(--gray1)",
        "&.Mui-checked": { color: "var(--green)" },
      }}
    />
    <Box>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: "var(--dark)",
          lineHeight: "24px",
        }}
      >
        {props.label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: "var(--gray1)",
          lineHeight: "20px",
          mt: "2px",
        }}
      >
        {props.description}
      </Typography>
    </Box>
  </Box>
);

const nameMatches = (typed: string, name: string) =>
  typed.trim() === name.trim();

const SiteBody = (props: { name: string; preview: StructureDeletePreview }) => {
  const { preview } = props;
  // nothing to lose by mistake on a site with no records, so no typing
  const needsName = preview.records.total > 0;
  const [typed, setTyped] = useState("");

  useEffect(() => {
    setConfirmButton(
      text("Structure-Delete-Site-Title"),
      RED,
      !needsName || nameMatches(typed, props.name),
    );
  }, [typed, needsName, props.name]);

  return (
    <Box display="flex" flexDirection="column" gap="18px">
      <Header title={text("Structure-Delete-Site-Title")}>
        <Lead
          lead={text("Structure-Delete-Lead")}
          name={props.name}
          tail={text("Structure-Delete-Site-Tail")}
        />
        <Facts>
          <Fact
            value={preview.assets}
            label={text("Structure-Delete-Assets")}
          />
          <Fact
            value={preview.records.total}
            label={recordsLabel(preview)}
            detail={statusLine(preview)}
          />
          <Fact
            value={preview.files.count}
            label={text("Structure-Delete-Files-Returned", {
              size: FileUtils.formatFileSize(preview.files.bytes),
            })}
          />
          <Fact
            value={preview.templates}
            label={text("Structure-Delete-Templates")}
          />
        </Facts>
      </Header>
      <Warnings preview={preview} />
      {needsName && (
        <TypedName name={props.name} value={typed} onChange={setTyped} />
      )}
      <Undo />
    </Box>
  );
};

type AssetChoice = { deleteRecords: boolean };

const AssetBody = (props: {
  name: string;
  preview: StructureDeletePreview;
  recordsOnly: boolean;
  onChoice: (deleteRecords: boolean) => void;
}) => {
  const { name, preview, recordsOnly, onChoice } = props;
  const hasRecords = preview.records.total > 0;
  const [deleteRecords, setDeleteRecords] = useState(recordsOnly);
  const [typed, setTyped] = useState("");
  const needsName = deleteRecords && hasRecords;

  useEffect(() => {
    onChoice(deleteRecords);
    const label = recordsOnly
      ? text("Structure-Delete-Records-Button")
      : deleteRecords
      ? text("Structure-Delete-Asset-And-Records")
      : text("Delete-Asset");
    setConfirmButton(
      label,
      deleteRecords ? RED : NAVY,
      !needsName || nameMatches(typed, name),
    );
  }, [deleteRecords, typed, needsName, recordsOnly, name, onChoice]);

  return (
    <Box display="flex" flexDirection="column" gap="18px">
      <Header
        title={
          recordsOnly
            ? text("Structure-Delete-Records-Title")
            : text("Delete-Asset")
        }
      >
        {recordsOnly ? (
          <Lead
            lead={text("Structure-Delete-Records-Lead")}
            name={name}
            tail={text("Structure-Delete-Records-Tail")}
          />
        ) : (
          <Lead
            lead={text("Structure-Delete-Lead")}
            name={name}
            tail={text("Structure-Delete-Asset-Tail")}
          />
        )}
        {(hasRecords || preview.files.count > 0) && (
          <Facts>
            <Fact
              value={preview.records.total}
              label={recordsLabel(preview)}
              detail={deleteRecords ? statusLine(preview) : undefined}
            />
            <Fact
              value={preview.files.count}
              label={text("Structure-Delete-Files", {
                size: FileUtils.formatFileSize(preview.files.bytes),
              })}
            />
          </Facts>
        )}
      </Header>
      {/* with no records there is nothing to keep, so there is nothing to choose */}
      {hasRecords && !recordsOnly && (
        <Box display="flex" flexDirection="column" gap="12px">
          <Option
            selected={!deleteRecords}
            label={text("Structure-Delete-Keep")}
            description={text("Structure-Delete-Keep-Description")}
            onSelect={() => setDeleteRecords(false)}
          />
          <Option
            selected={deleteRecords}
            label={text("Structure-Delete-With-Records")}
            description={text("Structure-Delete-With-Records-Description")}
            onSelect={() => setDeleteRecords(true)}
          />
        </Box>
      )}
      {deleteRecords && <Warnings preview={preview} />}
      {needsName && <TypedName name={name} value={typed} onChange={setTyped} />}
      {deleteRecords && <Undo />}
    </Box>
  );
};

const fireDeleteDialog = (
  body: ReactElement,
  confirmLabel: string,
  confirmColor: string,
) =>
  MySwal.fire({
    width: 600,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: confirmLabel,
    confirmButtonColor: confirmColor,
    cancelButtonText: text("Cancel"),
    customClass: { cancelButton: "cancel-btn-outlined" },
    html: body,
  });

/** Resolves true only when the user confirmed deleting the site. */
export const confirmSiteDelete = async (
  name: string,
  preview: StructureDeletePreview,
) => {
  const result = await fireDeleteDialog(
    <SiteBody name={name} preview={preview} />,
    text("Structure-Delete-Site-Title"),
    RED,
  );
  return result.isConfirmed === true;
};

/** Resolves the chosen mode, or null when cancelled; recordsOnly is for an asset already deleted with its records kept. */
export const confirmAssetDelete = async (
  name: string,
  preview: StructureDeletePreview,
  recordsOnly = false,
): Promise<AssetChoice | null> => {
  let deleteRecords = recordsOnly;
  const onChoice = (value: boolean) => {
    deleteRecords = value;
  };
  const result = await fireDeleteDialog(
    <AssetBody
      name={name}
      preview={preview}
      recordsOnly={recordsOnly}
      onChoice={onChoice}
    />,
    recordsOnly
      ? text("Structure-Delete-Records-Button")
      : text("Delete-Asset"),
    recordsOnly ? RED : NAVY,
  );
  if (result.isConfirmed !== true) return null;
  // an asset with nothing to keep goes off the list, and any file an earlier delete left behind is freed
  return { deleteRecords: deleteRecords || preview.records.total === 0 };
};
